import React from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useTheme } from '@mui/material/styles';
import type { Option } from './type';

type Props = {
  label: string;
  options: Option[];
  values: string[];
  onChange: (next: string[]) => void;
};

const UncheckedIcon = ({ borderColor = '#AAAAAA' }: any) => (
  <Box
    sx={{
      width: 18,
      height: 18,
      borderRadius: '4px',
      border: `1px solid ${borderColor}`,
      backgroundColor: '#fff',
      boxSizing: 'border-box',
    }}
  />
);

const CheckedIconBox = ({
  borderColor = '#39ACE7',
  checkColor = '#39ACE7',
  backgroundColor = '#fff',
}: any) => (
  <Box
    sx={{
      width: 18,
      height: 18,
      borderRadius: '4px',
      border: `1px solid ${borderColor}`,
      backgroundColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
    }}
  >
    <CheckIcon
      sx={{
        '&&': {
          fontSize: 14,
          color: `${checkColor} !important`,
          fill: `${checkColor} !important`,
        },
      }}
    />
  </Box>
);

export default function CheckboxGroup({ label, options, values, onChange }: Props) {
  const theme = useTheme();
  const colors = theme.palette;

  const allOption = options.find((option) => option.value === 'all');

  const optionsWithoutAll = options.filter(
    (option) => option.value !== 'all' && option.value !== 'ALL',
  );

  /**
   * If selected value is ['all'], UI should show all child items checked.
   * But actual stored value remains ['all'].
   */
  const effectiveValues =
    allOption && values.includes(allOption.value)
      ? optionsWithoutAll.map((option) => option.value)
      : values;

  const isAllChecked = allOption
    ? values.includes(allOption.value) ||
      optionsWithoutAll.every((option) => values.includes(option.value))
    : optionsWithoutAll.length > 0 &&
      optionsWithoutAll.every((option) => values.includes(option.value));

  const toggle = (selectedValue: string) => {
    /**
     * Case 1:
     * User clicked actual All checkbox.
     * If already selected, clear it.
     * Otherwise pass only ['all'].
     */
    if (selectedValue === 'all' || selectedValue === 'ALL') {
      if (isAllChecked) {
        onChange([]);
        return;
      }

      if (allOption) {
        onChange([allOption.value]);
        return;
      }

      onChange(optionsWithoutAll.map((option) => option.value));
      return;
    }

    /**
     * Case 2:
     * If current value is ['all'] and user clicks one child,
     * first expand ['all'] into all child values, then toggle selected child.
     */
    const currentValues =
      allOption && values.includes(allOption.value)
        ? optionsWithoutAll.map((option) => option.value)
        : values.filter((value) => value !== 'all' && value !== 'ALL');

    const nextValues = currentValues.includes(selectedValue)
      ? currentValues.filter((value) => value !== selectedValue)
      : [...currentValues, selectedValue];

    const isEveryChildSelected =
      optionsWithoutAll.length > 0 &&
      optionsWithoutAll.every((option) => nextValues.includes(option.value));

    /**
     * Case 3:
     * If user manually selected every child item,
     * convert it back to ['all'] when actual all option exists.
     */
    if (allOption && isEveryChildSelected) {
      onChange([allOption.value]);
      return;
    }

    onChange(nextValues);
  };

  return (
    <Box>
      <Typography
        sx={{
          '&&': {
            fontSize: 14,
            fontWeight: 500,
            color: colors.checkboxGroup.title,
            mb: 0.5,
          },
        }}
      >
        {label}
      </Typography>

      <Box sx={{ pl: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {options.map((o) => {
          const checked =
            o.value === 'all' || o.value === 'ALL'
              ? isAllChecked
              : effectiveValues.includes(o.value);

          return (
            <FormControlLabel
              key={o.value}
              control={
                <Checkbox
                  checked={checked}
                  onChange={() => toggle(o.value)}
                  disableRipple
                  icon={<UncheckedIcon borderColor="#AAAAAA" />}
                  checkedIcon={
                    <CheckedIconBox
                      borderColor={colors.checkboxGroup.checkboxBorder || '#39ACE7'}
                      checkColor={colors.checkboxGroup.checkboxBorder || '#39ACE7'}
                    />
                  }
                  sx={{
                    '&&': { p: 0, mr: 1 },
                  }}
                />
              }
              label={o.label}
              sx={{
                '&&': { mr: 2 },
                '&& .MuiFormControlLabel-label': {
                  fontSize: 14,
                  fontWeight: 400,
                  color: colors.checkboxGroup.optionText,
                },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}